import { BaseProps } from '../common/props';
import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { cs } from 'date-fns/locale';
import {
  parseISO,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  endOfWeek,
  setMonth,
  format,
  addDays,
  setYear,
} from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../button/Button';
import { Icon } from '../icon/Icon';
import { mcNavigateNext, mcNavigatePrev } from '@mc/react-ui-icons';
import {
  formatTimeViewDateValue,
  parseTimeStrValueToISO,
  TimeInput,
} from './TimeInput';
import { Space } from '../space/Space';
import { Menu } from '../menu/Menu';
import { MenuItem } from '../menu/MenuItem';
import { Label } from '../label/Label';
import { Card } from '../card/Card';
import { Block } from '../block/Block';
import { Col, Row } from '../row';
import { CloseButton } from '../button/CloseButton';
import { useYearListings } from '../calendar/useYearListings';
import { addMonths } from 'date-fns';

export type DatePickerProps = BaseProps & {
  value?: Date;
  onChange?: (date: Date) => void;
  locale?: Locale;
  // Voláno v případě, že se vybere konrkténí den (nokoli při změně např. měsíce nebo roku)
  onClose?: () => void;
  showTime?: boolean;
};

const computeShowForDate = (value?: Date) => {
  let tv = value ? new Date(value.getTime()) : undefined;
  let result = tv || new Date();
  result.setHours(0, 0, 0, 0);
  return result;
};

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  className,
  locale = cs,
  onClose,
  onChange,
  children,
  showTime,
  ...other
}) => {
  const useDateFormat = showTime ? 'P p' : 'P';

  const showConfirm = showTime;

  const [currentValue, setCurrentValue] = useState(value);
  useEffect(() => {
    setCurrentValue(value);
  }, [value]);
  const currentValueRef = useRef<Date>();
  currentValueRef.current = currentValue;

  const lastDayInMonth = useRef(false);
  useEffect(() => {
    if (!currentValue) {
      lastDayInMonth.current = false;
    } else {
      lastDayInMonth.current =
        currentValue.getDate() === endOfMonth(currentValue).getDate();
    }
  }, []);

  const today = useMemo(() => {
    let v = new Date();
    v.setHours(0, 0, 0, 0);
    return v;
  }, []);

  const monthNames = useMemo(() => {
    const list = [];
    for (let a = 0; a < 12; a++) {
      const dt = new Date(2000, a, 1);
      list.push({ id: a + 1, name: format(dt, 'LLLL', { locale }) });
    }

    return list;
  }, []);

  const showForDateRef = useRef<Date>();
  const [showForDate, setShowForDate] = useState(() => {
    return computeShowForDate(currentValue);
  });
  showForDateRef.current = showForDate;

  const valueWithoutTime = useMemo(() => {
    if (currentValue) {
      const dt = new Date(currentValue.getTime());
      dt.setHours(0, 0, 0, 0);
      return dt;
    } else {
      return undefined;
    }
  }, [currentValue]);

  const showForDateWithoutTime = useMemo(() => {
    const dt = new Date(showForDate.getTime());
    dt.setHours(0, 0, 0, 0);
    return dt;
  }, [showForDate]);

  const [timeValue, setTimeValue] = useState(() => {
    if (showTime) {
      if (value) {
        return new Date(value.getTime());
      } else {
        return showForDate;
      }
    } else {
      return new Date();
    }
  });

  useEffect(() => {
    setShowForDate(computeShowForDate(currentValue));
  }, [today, currentValue]);

  const start = startOfWeek(startOfMonth(showForDate), { locale });
  const end = endOfWeek(endOfMonth(showForDate), { locale });

  const handleSelectDay = (date: Date) => {
    let nd = new Date(date.getTime());
    if (showTime) {
      const use = currentValue || showForDate;
      nd.setHours(use.getHours(), use.getMinutes(), use.getSeconds());
    }

    if (!showConfirm) {
      onChange?.(nd);
      onClose?.();
    } else {
      setCurrentValue(nd);
      currentValueRef.current = nd;

      lastDayInMonth.current = nd.getDate() === endOfMonth(nd).getDate();
    }
  };

  const handleChangeYear = (year: number) => {
    if (showForDateRef.current) {
      let nd = new Date(showForDateRef.current.getTime());
      nd = setYear(nd, year);
      if (showTime) {
        const use = currentValue || showForDate;
        nd.setHours(use.getHours(), use.getMinutes(), use.getSeconds());
      }

      if (lastDayInMonth.current) {
        nd = endOfMonth(nd);
      }

      setShowForDate(nd);
    }
  };

  const handleChangeTime = (timeStr?: string) => {
    if (timeStr) {
      const time = parseTimeStrValueToISO(timeStr);
      if (time) {
        const tm = parseISO(time);
        let nd = new Date(timeValue.getTime());
        nd.setHours(tm.getHours(), tm.getMinutes(), tm.getSeconds());

        setTimeValue(nd);
      }
    }
  };

  const handleChangeMonth = (month?: number) => {
    if (month !== undefined && showForDateRef.current) {
      let nd = new Date(showForDateRef.current.getTime());
      nd = setMonth(nd, month);
      if (showTime) {
        const use = currentValue || showForDate;
        nd.setHours(use.getHours(), use.getMinutes(), use.getSeconds());
      }

      if (lastDayInMonth.current) {
        nd = endOfMonth(nd);
      }

      setShowForDate(nd);
    }
  };

  const handlePrevMonth = () => {
    const newDate = addMonths(showForDate, -1);
    setShowForDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = addMonths(showForDate, 1);
    setShowForDate(newDate);
  };

  const handleConfirm = () => {
    if (currentValue) {
      const nd = currentValue;
      if (showTime) {
        const use = timeValue;
        nd.setHours(use.getHours(), use.getMinutes(), use.getSeconds());
      }

      onChange?.(nd);
      onClose?.();
    }
  };

  const days = [];
  let day = start;

  while (+day < +end) {
    const renderDay = day;
    const renderTitleDay = new Date(day.getTime());
    if (currentValue) {
      renderTitleDay.setHours(
        currentValue.getHours(),
        currentValue.getMinutes(),
        currentValue.getSeconds()
      );
    }
    days.push(
      <div
        key={day.getTime()}
        className={classNames(Classes.DATE_PICKER_DAY, {
          [Classes.DATE_PICKER_DAY_CM]:
            renderDay.getMonth() === showForDate.getMonth(),
          [Classes.DATE_PICKER_DAY_TD]: +renderDay === +today,
          [Classes.ACTIVE]:
            valueWithoutTime && +renderDay === +valueWithoutTime,
          [Classes.MAYBE_ACTIVE]:
            valueWithoutTime &&
            +valueWithoutTime !== +showForDateWithoutTime &&
            +renderDay === +showForDateWithoutTime,
        })}
        title={format(renderTitleDay, useDateFormat, { locale })}
        onClick={() => handleSelectDay(renderDay)}
      >
        {renderDay.getDate()}
      </div>
    );

    day = addDays(day, 1);
  }

  const showMonth = showForDate.getMonth();
  const showYear = showForDate.getFullYear();
  const selectedMonth = monthNames.find((a) => a.id === showMonth + 1);
  const titledSelectedMonth =
    (selectedMonth?.name[0].toUpperCase() ?? '') +
    (selectedMonth?.name.slice(1) ?? '');

  const yearListings = useYearListings(showYear);

  return (
    <div className={classNames(className, Classes.DATE_PICKER_NEW)} {...other}>
      <div className={Classes.DATE_PICKER_HEADER}>
        <Space size={'1'} className={Classes.DATE_PICKER_HEADER_1}>
          <Button
            style={'link'}
            icon={<Icon fixedWidth icon={mcNavigatePrev} />}
            onClick={handlePrevMonth}
            dataTest={'prev-month'}
          ></Button>
          <Button
            fill
            textCenter
            hideMenuDropdownIcon
            style={'link'}
            popupProps={{ nested: true }}
            menu={
              <Menu>
                {monthNames.map((month) => (
                  <MenuItem
                    active={showMonth + 1 === month.id}
                    key={month.id}
                    text={month.name}
                    onClick={() => handleChangeMonth(month.id - 1)}
                  />
                ))}
              </Menu>
            }
          >
            {titledSelectedMonth || '-'}
          </Button>
          <Button
            style={'link'}
            icon={<Icon fixedWidth icon={mcNavigateNext} />}
            onClick={handleNextMonth}
            dataTest={'next-month'}
          ></Button>
        </Space>
        <Space size={'1'} className={Classes.DATE_PICKER_HEADER_2}>
          <Button
            style={'link'}
            menu={() => (
              <Menu style={{ maxHeight: 300, overflowY: 'auto' }}>
                {yearListings.map((year) => (
                  <MenuItem
                    active={showYear === year}
                    text={year}
                    onClick={() => handleChangeYear(year)}
                  />
                ))}
              </Menu>
            )}
          >
            {showYear}
          </Button>
        </Space>
      </div>
      <div className={Classes.DATE_PICKER_DAY_NAMES}>
        {Array.from(Array(7)).map((_, i) => {
          let dayName = format(addDays(start, i), 'EEEEEE', { locale });
          dayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
          return <Label key={i}>{dayName}</Label>;
        })}
      </div>
      <div className={Classes.DATE_PICKER_DAYS}>{days}</div>
      {showTime && timeValue && (
        <div className={Classes.DATE_PICKER_TIME}>
          <Card mode={'edit'} className={'w-100'}>
            <Block bodyClassName={'justify-content-center d-flex p-1'}>
              <TimeInput
                onlyTime
                value={formatTimeViewDateValue(timeValue)}
                onChange={handleChangeTime}
              />
            </Block>
          </Card>
        </div>
      )}
      {showConfirm && (
        <Row noGutters>
          <Col>
            <CloseButton
              onClick={() => {
                onClose?.();
              }}
            >
              Zrušit
            </CloseButton>
          </Col>
          <Col xs={'auto'}>
            <Button
              disabled={!currentValue}
              style={'primary'}
              onClick={handleConfirm}
            >
              Potvrdit
            </Button>
          </Col>
        </Row>
      )}
    </div>
  );
};
